import { motion } from 'framer-motion';
import LocalizedLink from '@/components/LocalizedLink';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Award, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import AnimatedCounter from '@/components/ui/AnimatedCounter';
import heroImage from '@/assets/hero-home-seafood-premium.jpg';
import heroMobileImage from '@/assets/hero-mobile.jpg';

const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <Helmet>
        <link rel="preload" as="image" href={heroMobileImage} media="(max-width: 767px)" />
        <link rel="preload" as="image" href={heroImage} media="(min-width: 768px)" />
      </Helmet>
      {/* Background Image */}
      <div className="absolute inset-0">
        <picture>
          <source media="(max-width: 767px)" srcSet={heroMobileImage} />
          <img
            src={heroImage}
            alt={t('hero.imageAlt')}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-hero" />
      </div>

      {/* Content */}
      <div className="relative container-premium pt-32 pb-20">
        <div className="max-w-4xl">
          {/* Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap gap-4 mb-8"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 backdrop-blur-sm rounded-full text-sm font-medium text-accent border border-accent/30 shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
              <Award size={16} />
              {t('hero.labelRouge')}
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-foreground/10 backdrop-blur-sm rounded-full text-sm font-medium text-foreground border border-foreground/20 shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
              <Shield size={16} />
              {t('hero.traceability')}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
            style={{ textShadow: '2px 2px 10px rgba(0,0,0,0.8)' }}
          >
            {t('hero.headline')}{' '}
            <span className="text-accent">{t('hero.headlineHighlight')}</span>{' '}
            {t('hero.headlineEnd')}
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl text-foreground/80 mb-8 leading-relaxed max-w-2xl font-semibold break-words"
            style={{ textShadow: '1px 1px 6px rgba(0,0,0,0.7)' }}
          >
            <AnimatedCounter end={30} suffix=" ans" className="font-semibold text-accent" /> {t('hero.subheadline')}
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-muted-foreground mb-10 max-w-xl font-medium"
            style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.6)' }}
          >
            {t('hero.description')}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-wrap gap-4"
          >
            <LocalizedLink to="/contact" onClick={() => window.scrollTo({ top: 0 })}>
              <Button variant="hero-outline" size="xl">
                {t('hero.cta')}
                <ArrowRight size={20} />
              </Button>
            </LocalizedLink>
            <LocalizedLink to="/produits" onClick={() => window.scrollTo({ top: 0 })}>
              <Button variant="hero-outline" size="xl">
                {t('hero.ctaProducts')}
              </Button>
            </LocalizedLink>
            <LocalizedLink to="/partenaires" onClick={() => window.scrollTo({ top: 0 })}>
              <Button variant="hero" size="xl">
                {t('hero.ctaPartners')}
              </Button>
            </LocalizedLink>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            { value: 30, suffix: '+', label: t('hero.stats.years') },
            { value: 5, suffix: '+', label: t('hero.stats.countries') },
            { value: 100, suffix: '%', label: t('hero.stats.traceability') },
            { value: 24, suffix: 'h', label: t('hero.stats.freshness') },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="font-serif text-3xl md:text-4xl font-bold text-accent mb-2">
                <AnimatedCounter end={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 rounded-full border-2 border-foreground/30 flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 rounded-full bg-accent"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
